import React, { useCallback, useRef, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import { ATTACHMENT_EXTENSIONS, ATTACHMENT_MAX_NUM } from 'constants/Attachment'
import { PREVIEW_FILE_ROUTES } from 'config/routes'
import useAttachmentFileDownload from 'hooks/useAttachmentFileDownload'
import type { Attachment, Booking } from 'repositories/BerthApi/types/booking'
import {
  canPreviewFileAttachment,
  determineIconOfFileName,
  extractExtensionOfFileName,
} from 'utils/commonFunctions'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'

import type {
  EditBookingAttachment,
  EditBookingFormValues,
  EditBookingResolverContext,
} from '../../../utils/editBookingValidation'
import { validateAttachments } from '../../../utils/validateAttachments'

import styles from './AttachmentSection.module.css'

type Props = {
  form: UseFormReturn<EditBookingFormValues, EditBookingResolverContext>
  booking: Booking | null
  disabled: boolean
  onNotify: (payload: SnackbarPayload) => void
}

const readAsDataURL = (file: File): Promise<EditBookingAttachment> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve({
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        content: typeof reader.result === 'string' ? reader.result : '',
      })
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'))
    reader.readAsDataURL(file)
  })

export const AttachmentSection: React.FC<Props> = ({ form, booking, disabled, onNotify }) => {
  const { watch, setValue } = form
  const attachments = watch('attachments')
  const deleteAttachmentIds = watch('deleteAttachmentIds')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { onClickFileDownload } = useAttachmentFileDownload(false)

  const remaining = ATTACHMENT_MAX_NUM - attachments.length
  const addDisabled = disabled || attachments.length >= ATTACHMENT_MAX_NUM

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      // input は同じファイルを再選択しても onChange が走るようリセットする
      if (inputRef.current) inputRef.current.value = ''
      if (files.length === 0) return

      const result = validateAttachments(attachments, files)
      if (!result.ok) {
        setErrorMessage(result.message)
        onNotify({ message: result.message, variant: 'error' })
        return
      }
      setErrorMessage('')

      try {
        const next = await Promise.all(files.map(readAsDataURL))
        setValue('attachments', [...attachments, ...next], { shouldDirty: true })
      } catch {
        const message = 'ファイルの読み込みに失敗しました'
        setErrorMessage(message)
        onNotify({ message, variant: 'error' })
      }
    },
    [attachments, setValue, onNotify]
  )

  const handleRemove = useCallback(
    (target: EditBookingAttachment) => {
      const nextAttachments = attachments.filter((a) => a.name !== target.name)
      setValue('attachments', nextAttachments, { shouldDirty: true })
      if (target.id != null) {
        setValue('deleteAttachmentIds', [...deleteAttachmentIds, target.id], { shouldDirty: true })
      }
      setErrorMessage('')
    },
    [attachments, deleteAttachmentIds, setValue]
  )

  // 既存添付のダウンロード hook は repositories の Attachment 型を要求するが、
  // 内部で参照するのは id / name のみ。booking.attachments から元 Attachment を探して渡す。
  const findOriginalAttachment = (id: number): Attachment | undefined =>
    booking?.attachments?.find((a) => a.id === id)

  const renderFileName = (a: EditBookingAttachment) => {
    if (a.id == null) {
      // 未保存ファイル: テキスト表示のみ
      return <span className={styles.fileName}>{a.name}</span>
    }
    const original = findOriginalAttachment(a.id)
    if (canPreviewFileAttachment(a.name)) {
      return (
        <a
          className={styles.fileLink}
          href={`${PREVIEW_FILE_ROUTES.normal}/${a.id}/${extractExtensionOfFileName(a.name)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {a.name}
        </a>
      )
    }
    return (
      <button
        type="button"
        className={styles.fileLink}
        onClick={(e) => {
          if (!original) return
          void onClickFileDownload(e, original)
        }}
      >
        {a.name}
      </button>
    )
  }

  return (
    <div className={styles.subSection}>
      <div className={styles.header}>
        <span className={styles.remainingText}>あと {Math.max(remaining, 0)} 個追加できます</span>
        <label
          htmlFor="attachmentFileInput"
          className={`${styles.addButton} ${addDisabled ? styles.addButtonDisabled : ''}`}
          aria-disabled={addDisabled}
        >
          追加
        </label>
        <input
          id="attachmentFileInput"
          ref={inputRef}
          type="file"
          className={styles.hiddenInput}
          accept={ATTACHMENT_EXTENSIONS.join(',')}
          multiple
          disabled={addDisabled}
          onChange={handleChange}
        />
      </div>

      {attachments.length > 0 && (
        <ul className={styles.fileList}>
          {attachments.map((a) => (
            <li key={`${a.id ?? 'new'}-${a.name}`} className={styles.fileItem}>
              <span className={styles.fileMain}>
                <i className={`${determineIconOfFileName(a.name)} ${styles.fileIcon}`} />
                {renderFileName(a)}
              </span>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => handleRemove(a)}
                disabled={disabled}
                aria-label={`${a.name} を削除`}
              >
                <i className="far fa-trash-alt" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {errorMessage && <span className={styles.errorText}>{errorMessage}</span>}
    </div>
  )
}
